import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Form from '@radix-ui/react-form';
import { Button } from '@quero-delivery/quero-components-web';
import {
  getEndOfDay,
  getStartOfDay,
  isSameHour,
} from '@querodelivery/qd-date-helper';
import { PatternFormat } from 'react-number-format';
import { AccessPermission } from '@/_components/access-permission';
import { useAppDispatch } from '@/_store/hooks';
import type { FilterProps } from './types';
import type { TypeMotoboysListForm } from '../../types';
import { MOTOBOYS_FORM_LIST_SCHEMA, STATUS_MOTOBOY } from '../../constants';
import styles from './styles.module.scss';
import useScreenType from '@/hooks/util/useScreenType';
import { ResetBalance } from '../reset-balance';
import { motoboyInfoSuccess } from '@/_store/modules/motoboy-info';
import type { ENUM_DELIVERY_TYPE } from '@/_features/deliveries/constants';
import { TYPE_DELIVERY_OPTIONS } from '@/_features/deliveries/constants';
import {
  setFilterCityOnDeliverymanList,
  setFilterDeliveryTypeOnDeliverymanList,
} from '@/_store/modules/filters/slice';
import { useCities } from '@/_hooks/use-cities';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import { CalendarInput } from '@/_components/calendar-input/calendar-input';

export function Filter({ params, onRequest }: FilterProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { CITIES_OPTIONS, loadingCities } = useCities();

  const isMobile = useScreenType('mobile');

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TypeMotoboysListForm>({
    resolver: yupResolver(MOTOBOYS_FORM_LIST_SCHEMA),
    defaultValues: params,
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  function submitForm(data: TypeMotoboysListForm) {
    onRequest(data);
  }

  function handleChangePageCreateMotoboy() {
    dispatch(motoboyInfoSuccess(null));
    navigate('/entregadores/novo');
  }

  return (
    <>
      <Form.Root
        className={styles.motoboysListForm}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={styles.line}>
          <Controller
            name="deliverymanId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="deliverymanId"
                value={value}
                containerClassName={styles.containerInput}
                width="100%"
                onChange={onChange}
                label="Id do entregador"
                placeholder="entregador id"
                errorMessage={errors.deliverymanId?.message}
              />
            )}
          />
          <Controller
            name="cellphone"
            control={control}
            render={({ field: { onChange, value } }) => (
              <PatternFormat
                name="cellphone"
                value={value}
                containerClassName={styles.containerInput}
                width="100%"
                format="(##) #####-####"
                onValueChange={({ value }) => onChange(value)}
                label="Telefone"
                placeholder="(99) 99999-9999"
                customInput={TextInput}
                errorMessage={errors.cellphone?.message}
              />
            )}
          />

          <Controller
            name="cpf"
            control={control}
            render={({ field: { onChange, value } }) => (
              <PatternFormat
                name="cpf"
                value={value}
                containerClassName={styles.containerInput}
                width="100%"
                format="###.###.###-##"
                onValueChange={({ value }) => onChange(value)}
                label="CPF"
                placeholder="CPF"
                customInput={TextInput}
                errorMessage={errors.cpf?.message}
              />
            )}
          />

          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="name"
                value={value}
                containerClassName={styles.containerInput}
                width="100%"
                onChange={onChange}
                label="Nome"
                placeholder="Nome"
                errorMessage={errors.name?.message}
              />
            )}
          />

          <Controller
            name="startDate"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div>
                <CalendarInput
                  name="startDate"
                  value={value}
                  label={isMobile ? 'Data inicial' : 'Data inicial de cadastro'}
                  selectsStart
                  startDate={value}
                  endDate={endDate}
                  onChange={(date: Date | null) => {
                    if (!date) {
                      return onChange(undefined);
                    }

                    if (isSameHour(date, getStartOfDay(date))) {
                      return onChange(getStartOfDay(date));
                    }

                    onChange(date);
                  }}
                  withPortal={isMobile}
                  maxDate={new Date()}
                  todayButton="Hoje"
                  timeInputLabel="Horário:"
                  showTimeInput
                  clearable
                  popperPlacement="top"
                />

                {errors.startDate ? (
                  <span className="text-error">{errors.startDate.message}</span>
                ) : null}
              </div>
            )}
          />

          <Controller
            name="endDate"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div>
                <CalendarInput
                  name="endDate"
                  value={value}
                  label={isMobile ? 'Data final' : 'Data final de cadastro'}
                  minDate={startDate || undefined}
                  startDate={startDate}
                  endDate={value}
                  onChange={(date: Date | null) => {
                    if (!date) {
                      return onChange(undefined);
                    }

                    if (isSameHour(date, getStartOfDay(date))) {
                      return onChange(getEndOfDay(date));
                    }

                    onChange(date);
                  }}
                  withPortal={isMobile}
                  maxDate={new Date()}
                  todayButton="Hoje"
                  timeInputLabel="Horário:"
                  showTimeInput
                  clearable
                  popperPlacement="top"
                />
                {errors.endDate ? (
                  <span className="text-error">{errors.endDate.message}</span>
                ) : null}
              </div>
            )}
          />

          <Controller
            name="cityId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                label="Município"
                options={CITIES_OPTIONS}
                value={value}
                search
                placeholder="Filtrar por município"
                onChange={value => {
                  if (typeof value === 'string') {
                    dispatch(setFilterCityOnDeliverymanList(value));
                  }
                  onChange(value);
                }}
                loading={loadingCities}
              />
            )}
          />

          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                label="Status"
                options={STATUS_MOTOBOY}
                value={value as string[]}
                search
                multiple
                placeholder="Filtrar por status"
                onChange={value => {
                  onChange(value);
                }}
              />
            )}
          />

          <Controller
            name="deliveryType"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                label="Tipo de entregador"
                options={TYPE_DELIVERY_OPTIONS}
                value={value}
                search
                clearable
                placeholder="Selecionar filtro"
                onChange={data => {
                  const newDeliveryType = data as ENUM_DELIVERY_TYPE;
                  dispatch(
                    setFilterDeliveryTypeOnDeliverymanList(newDeliveryType),
                  );
                  onChange(newDeliveryType);
                }}
              />
            )}
          />
          <div className={styles.containerButtons}>
            <Button
              className={styles.buttonSearch}
              textFirst="Buscar"
              firstStrong
              type="submit"
            />
          </div>
          <div className={styles.containerButtons}>
            <AccessPermission permissionKey="deliveryman.createDeliveryman">
              <Button
                type="button"
                className={styles.buttonNewDeliveryman}
                textEnd="Adicionar novo entregador"
                size="sm"
                onClick={handleChangePageCreateMotoboy}
              />
            </AccessPermission>
            <ResetBalance />
          </div>
        </div>
      </Form.Root>
    </>
  );
}
